"use client";

import Container from "@/components/Container";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import GiftIcon from "@/public/assets/images/gift.svg";
import Text from "@/components/Text";
import { transformText } from "@/utils/transformText";
import GradientLightText from "@/components/GradientLightText";
import BaliPriceBox from "@/components/BaliPriceBox";
import { ProductType, ProductTypeType } from "@/types/Products";
import { isFromYear2024 } from "@/utils/date";

type PricesMode = "single" | "double";

type TabType = {
  id: number;
  text: string;
  value: PricesMode;
};

type ButtonType = {
  text: string;
};

type DetailsButtonType = {
  text: string;
};

type ImageType = {
  src: {
    desktop: string;
    tablet: string;
    mobile: string;
  };
  alt: string;
};

export type ModalType = {
  isReservation?: boolean;
  subtitle: string;
  title: string;
  text: string;
  listTitle: string;
  items: string[];
  quote: string;
  price: string;
  button: ButtonType;
  image: ImageType;
  badge?: string;
};

export type PriceType = {
  type:
    | "singleTicket"
    | "singleReservation"
    | "doubleTicket"
    | "doubleReservation";
  badge?: string;
  priceFollowText?: string;
  title: string;
  text: string;
  button: ButtonType;
  detailsButton?: DetailsButtonType;
  modal?: ModalType;
};

type ContentType = {
  text: string;
  tabs: TabType[];
  prices: PriceType[];
};

type BaliPriceProps = {
  component: {
    type: string;
    content: ContentType;
    products: ProductType[];
  };
};

const dropIn = {
  hidden: {
    scale: 0.85,
    opacity: 0,
  },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.3,
      // type: "spring",
      // damping: 30,
      // stiffness: 100,
    },
  },
  exit: {
    scale: 0.85,
    opacity: 0,
    transition: {
      duration: 0,
    },
  },
};

export const returnProductTypeBasedOnComponentType = (
  type:
    | "singleTicket"
    | "singleReservation"
    | "doubleTicket"
    | "doubleReservation"
): ProductTypeType | undefined => {
  switch (type) {
    case "singleTicket":
      return ProductTypeType.EventTicket;
    case "singleReservation":
      return ProductTypeType.EventBooking;
    case "doubleTicket":
      return ProductTypeType.EventDoubleTicket;
    case "doubleReservation":
      return ProductTypeType.EventDoubleBooking;
  }
};

const BaliPrice = ({ component }: BaliPriceProps) => {
  const [activePriceMode, setActivePriceMode] = useState<PricesMode>("single");
  const singleTicket = component.content.prices.filter(
    (price) => price.type === "singleTicket"
  )[0];
  const singleReservation = component.content.prices.filter(
    (price) => price.type === "singleReservation"
  )[0];
  const doubleTicket = component.content.prices.filter(
    (price) => price.type === "doubleTicket"
  )[0];
  const doubleReservation = component.content.prices.filter(
    (price) => price.type === "doubleReservation"
  )[0];

  return (
    <div className="bali-prices" id="prices">
      <Container>
        <div className="bali-prices__content">
          <ul
            className={`bali-prices__tabs ${
              activePriceMode === "single"
                ? "bali-prices__tabs--single"
                : "bali-prices__tabs--double"
            }`.trim()}
          >
            {component.content.tabs.map((tab) => (
              <li
                className={`bali-prices__tab ${
                  tab.value === activePriceMode
                    ? "bali-prices__tab--active"
                    : ""
                }`.trim()}
                key={tab.id}
              >
                <button
                  className="bali-prices__button weight-700"
                  onClick={() => setActivePriceMode(tab.value)}
                >
                  {tab.text}
                  {tab.value === "double" ? <GiftIcon /> : null}
                </button>
              </li>
            ))}
          </ul>
          <div className="bali-prices__tab-content">
            <AnimatePresence
              initial={false}
              onExitComplete={() => null}
              mode="popLayout"
            >
              {activePriceMode === "single" ? (
                <motion.div
                  variants={dropIn}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <div className="bali-prices__tab-content-holder">
                    <BaliPriceBox
                      component={singleTicket}
                      product={
                        component.products.filter(
                          (product) =>
                            isFromYear2024(product.created_at!) &&
                            product.type ===
                              returnProductTypeBasedOnComponentType(
                                singleTicket.type
                              )
                        )[0]
                      }
                    />
                    <BaliPriceBox
                      isHighlighted
                      component={singleReservation}
                      product={
                        component.products.filter(
                          (product) =>
                            isFromYear2024(product.created_at!) &&
                            product.type ===
                              returnProductTypeBasedOnComponentType(
                                singleReservation.type
                              )
                        )[0]
                      }
                    />
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
            <AnimatePresence
              initial={false}
              onExitComplete={() => null}
              mode="popLayout"
            >
              {activePriceMode === "double" ? (
                <motion.div
                  variants={dropIn}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <div className="bali-prices__tab-content-holder center">
                    <BaliPriceBox
                      component={doubleTicket}
                      modal={doubleTicket.modal}
                      product={
                        component.products.filter(
                          (product) =>
                            isFromYear2024(product.created_at!) &&
                            product.type ===
                              returnProductTypeBasedOnComponentType(
                                doubleTicket.type
                              )
                        )[0]
                      }
                    />
                    <BaliPriceBox
                      isHighlighted
                      component={doubleReservation}
                      modal={doubleReservation.modal}
                      badge={doubleReservation.badge}
                      product={
                        component.products.filter(
                          (product) =>
                            isFromYear2024(product.created_at!) &&
                            product.type ===
                              returnProductTypeBasedOnComponentType(
                                doubleReservation.type
                              )
                        )[0]
                      }
                    />
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
        <Text
          element="h3"
          elementStyle="h2-secondary"
          fontFamily="noto-serif"
          className="weight-700 center text-center bali-prices__text"
        >
          {transformText(component.content.text, {
            gradient: (chunk) => (
              <GradientLightText element="span">{chunk}</GradientLightText>
            ),
          })}
        </Text>
      </Container>
    </div>
  );
};

export default BaliPrice;
